import { CustomPatternMatcherFunc, IToken, Lexer, TokenType, createToken } from 'chevrotain';
import { LexerContext } from '../common/LexerContext';
import {
  CLEF,
  CLEF_FLATTED,
  CLEF_SECONDARY,
  CLEF_SECONDARY_FLATTED,
  H_EPISEMA,
  NOTE_BIVIRGA,
  NOTE_DISTROPHA,
  NOTE_DISTROPHA_AUCTUS_ASCENDENS,
  NOTE_PUNCTUM, NOTE_PUNCTUM_FUSED,
  NOTE_PUNCTUM_INCLINATUM,
  NOTE_PUNCTUM_INCLINATUM_FUSED,
  NOTE_PUNCTUM_INCLINATUM_INITIO_DEBILIS,
  NOTE_PUNCTUM_INITIO_DEBILIS, NOTE_TRISTROPHA,
  NOTE_TRISTROPHA_AUCTUS_ASCENDENS, NOTE_TRIVIRGA,
  PUNCTUM_MORA,
  STRING_LITERAL,
  STRING_LITERAL_OPENING_TAG,
  STRING_LITERAL_CLOSING_TAG,

  B_DOMINICAN_BAR,
  B_VIRGULA_PAREN_HIGH,
  B_VIRGULA_PAREN,
  B_VIRGULA_HIGH,
  B_VIRGULA,
  B_DIVISIO_MINIMIS_HIGH,
  B_DIVISIO_MINIMIS,
  B_DIVISIO_MINIMA_PAREN_HIGH,
  B_DIVISIO_MINIMA_PAREN,
  B_DIVISIO_MINIMA_HIGH,
  B_DIVISIO_MINIMA,
  B_DIVISIO_MINOR,
  B_DIVISIO_FINALIS,
  B_DIVISIO_MAIOR_DOTTED,
  B_DIVISIO_MAIOR,

  TAIL_LIQUIESCENIA,
  TAIL_LIQUIESCENIA_AUCTUS_DESCENDENS,
  TAIL_LIQUIESCENIA_DEMINUTUS,

  S_QUADRATUM,
  S_ORISCUS_UNDETERMINED,
  S_ORISCUS_DESCENDENS,
  S_ORISCUS_ASCENDENS,
  S_ORISCUS_SCAPUS_UNDETERMINED,
  S_ORISCUS_SCAPUS_DESCENDENS,
  S_ORISCUS_SCAPUS_ASCENDENS,
  S_QUILISMA,
  S_QUILISMA_QUADRATUM,
  S_VIRGA,
  S_VIRGA_REVERSA,
  S_STROPHA,
  S_LINEA,
  S_LINEA_PUNCTUM,

  CAVUM,
  S_LINEA_PUNCTUM_CAVUM,

  S_FLAT,
  S_FLAT_PAREN,
  S_SHARP,
  S_SHARP_PAREN,
  S_NATURAL,
  S_NATURAL_PAREN,

  SP_HALF_SPACE,
  SP_INTERGLYPH_SPACE,
  SP_NEUMATIC_CUT,
  SP_GLYPH_SPACE,
  SP_LARGER_SPACE,
  SP_LARGER_SPACE_NB,
  SP_GLYPH_SPACE_NB,
  SP_ZERO_WIDTH,

  END_OF_LINE,
  END_OF_LINE_FORSES_CUSTOS,
  END_OF_LINE_FORSES_CUSTOS_ON,

  END_OF_LINE_RAGGED,
  END_OF_LINE_RAGGED_CUSTOS,
  END_OF_LINE_RAGGED_CUSTOS_ON,

  CUSTOS,
  MANUAL_CUSTOS,

  V_EPISEMA,
  WHITESPACE_SINGLE,

  NABC_COMPLEX_NEUME_SEPARATOR,
  NABC_HORIZONTAL_SPACING_ADJUSTMENT,
  NABC_GLYPH_MODIFIER,
  NABC_PITCH,

  NABC_BASIC_GLYPH_GALL,
  NABC_SUBPUNCTIS_AND_PREPUNCTIS_GALL,
  NABC_SIGNIFICANT_LETTER_GALL,

  NABC_BASIC_GLYPH_LAON,
  NABC_SUBPUNCTIS_AND_PREPUNCTIS_LAON,
  NABC_SIGNIFICANT_LETTER_LAON,
  NABC_SIGNIFICANT_LETTER_TIRONIAN_LAON,
} from '../common/LexerTokens';

const WHITESPACE_DELIMETER = createToken({
  name: 'WHITESPACE_DELIMETER',
  pattern: /(?<=\))\s/,
  line_breaks: true,
});

const R_PAREN_POPPING = createToken({
  name: 'R_PAREN',
  pattern: /\)/,
  pop_mode: true,
});

const L_PAREN_PUSHING_GABC = createToken({
  name: 'L_PAREN_PUSHING_GABC',
  pattern: /\(/,
  push_mode: 'gabc'
});

const L_PAREN_PUSHING_NABC_GALL = createToken({
  name: 'L_PAREN_PUSHING_NABC_GALL',
  pattern: /\(/,
  push_mode: 'nabc_gall'
});

const L_PAREN_PUSHING_NABC_LAON = createToken({
  name: 'L_PAREN_PUSHING_NABC_LAON',
  pattern: /\(/,
  push_mode: 'nabc_laon'
});

// state 0 = gabc
// state 1 = nabc
function getPipeMatcher(nabcLines: number, state: number): CustomPatternMatcherFunc {
  return (
    text: string,
    startOffset: number,
    tokens: IToken[],
    groups: { [groupName: string]: IToken[] }
  ) => {
    if (text[startOffset] !== '|') {
      return null;
    }

    let openingPrentIndex = startOffset - 1;
    let pipesCount = 0;
    while (text[openingPrentIndex] !== '(') {
      if (text[openingPrentIndex] === '|') {
        pipesCount++;
      }
      openingPrentIndex--;
    }

    const normalizedNabcLines = nabcLines;

    if (state === 0) { //gabc
      if ((pipesCount % normalizedNabcLines) === normalizedNabcLines) {
        return null;
      } else {
        return [text[startOffset]];
      }
    } else if (state === 1) { // nabc
      if ((pipesCount % normalizedNabcLines) !== normalizedNabcLines) {
        return [text[startOffset]];
      } else {
        return null;
      }
    }

    return null;
  };
}

const tokenCollections = {
  general: [
    R_PAREN_POPPING,

    STRING_LITERAL_CLOSING_TAG,
    STRING_LITERAL_OPENING_TAG,
    STRING_LITERAL,
    WHITESPACE_SINGLE,
  ],
  gabc: [
    R_PAREN_POPPING,

    CLEF,
    CLEF_FLATTED,
    CLEF_SECONDARY,
    CLEF_SECONDARY_FLATTED,

    MANUAL_CUSTOS,
    CUSTOS,

    NOTE_TRIVIRGA,
    NOTE_BIVIRGA,
    NOTE_TRISTROPHA_AUCTUS_ASCENDENS,
    NOTE_DISTROPHA_AUCTUS_ASCENDENS,
    NOTE_TRISTROPHA,
    NOTE_DISTROPHA,
    NOTE_PUNCTUM,
    NOTE_PUNCTUM_INITIO_DEBILIS,
    NOTE_PUNCTUM_FUSED,
    NOTE_PUNCTUM_INCLINATUM,
    NOTE_PUNCTUM_INCLINATUM_INITIO_DEBILIS,
    NOTE_PUNCTUM_INCLINATUM_FUSED,
    V_EPISEMA,
    H_EPISEMA,
    PUNCTUM_MORA,

    B_DOMINICAN_BAR,
    B_VIRGULA_PAREN_HIGH,
    B_VIRGULA_PAREN,
    B_VIRGULA_HIGH,
    B_VIRGULA,
    B_DIVISIO_MINIMIS_HIGH,
    B_DIVISIO_MINIMIS,
    B_DIVISIO_MINIMA_PAREN_HIGH,
    B_DIVISIO_MINIMA_PAREN,
    B_DIVISIO_MINIMA_HIGH,
    B_DIVISIO_MINIMA,
    B_DIVISIO_MINOR,
    B_DIVISIO_FINALIS,
    B_DIVISIO_MAIOR_DOTTED,
    B_DIVISIO_MAIOR,

    TAIL_LIQUIESCENIA_DEMINUTUS,
    TAIL_LIQUIESCENIA_AUCTUS_DESCENDENS,
    TAIL_LIQUIESCENIA,

    S_QUADRATUM,

    S_ORISCUS_DESCENDENS,
    S_ORISCUS_ASCENDENS,
    S_ORISCUS_UNDETERMINED,

    S_ORISCUS_SCAPUS_DESCENDENS,
    S_ORISCUS_SCAPUS_ASCENDENS,
    S_ORISCUS_SCAPUS_UNDETERMINED,

    S_QUILISMA,
    S_QUILISMA_QUADRATUM,
    S_VIRGA,
    S_VIRGA_REVERSA,
    S_STROPHA,

    S_FLAT,
    S_FLAT_PAREN,
    S_SHARP,
    S_SHARP_PAREN,
    S_NATURAL,
    S_NATURAL_PAREN,

    S_LINEA_PUNCTUM_CAVUM,
    CAVUM,
    S_LINEA_PUNCTUM,
    S_LINEA,

    SP_HALF_SPACE,
    SP_INTERGLYPH_SPACE,
    SP_NEUMATIC_CUT,
    SP_GLYPH_SPACE,
    SP_LARGER_SPACE,
    SP_LARGER_SPACE_NB,
    SP_GLYPH_SPACE_NB,
    SP_ZERO_WIDTH,

    END_OF_LINE_FORSES_CUSTOS,
    END_OF_LINE_FORSES_CUSTOS_ON,
    END_OF_LINE,

    END_OF_LINE_RAGGED_CUSTOS,
    END_OF_LINE_RAGGED_CUSTOS_ON,
    END_OF_LINE_RAGGED,

    WHITESPACE_SINGLE,
  ],
  nabc_gall: [
    R_PAREN_POPPING,

    NABC_COMPLEX_NEUME_SEPARATOR,
    NABC_HORIZONTAL_SPACING_ADJUSTMENT,
    NABC_BASIC_GLYPH_GALL,
    NABC_GLYPH_MODIFIER,
    NABC_PITCH,
    NABC_SUBPUNCTIS_AND_PREPUNCTIS_GALL,
    NABC_SIGNIFICANT_LETTER_GALL
  ],
  nabc_laon: [
    R_PAREN_POPPING,

    NABC_COMPLEX_NEUME_SEPARATOR,
    NABC_HORIZONTAL_SPACING_ADJUSTMENT,
    NABC_BASIC_GLYPH_LAON,
    NABC_GLYPH_MODIFIER,
    NABC_PITCH,
    NABC_SUBPUNCTIS_AND_PREPUNCTIS_LAON,
    NABC_SIGNIFICANT_LETTER_LAON,
    NABC_SIGNIFICANT_LETTER_TIRONIAN_LAON,
  ],
};

function LexerFactory(settings: LexerContext): {
  lexer: Lexer,
  pipes: { [key: string]: TokenType },
} {
  if (settings.nabcOnlyParser) {
    const NABC_BEGIN_PIPE = createToken({
      name: 'NABC_BEGIN_PIPE',
      pattern: /\|/,
    });
    const lexer = new Lexer({
      modes: {
        general: [
          WHITESPACE_DELIMETER,
          settings.nabcMode === 'gall' ? L_PAREN_PUSHING_NABC_GALL : L_PAREN_PUSHING_NABC_LAON,
        ].concat(tokenCollections.general),
        gabc: [],
        nabc_gall: [NABC_BEGIN_PIPE].concat(tokenCollections.nabc_gall),
        nabc_laon: [NABC_BEGIN_PIPE].concat(tokenCollections.nabc_laon),
      },
      defaultMode: 'general',
    });
    return { lexer: lexer, pipes: { NABC_BEGIN_PIPE } };
  } else {
    const NABC_BEGIN_PIPE = createToken({
      name: 'NABC_BEGIN_PIPE',
      pattern: getPipeMatcher(settings.nabcLines, 1),
      line_breaks: false,
      push_mode: 'nabc_' + settings.nabcMode,
      pop_mode: true,
    });

    const GABC_BEGIN_PIPE = createToken({
      name: 'GABC_BEGIN_PIPE',
      pattern: getPipeMatcher(settings.nabcLines, 0),
      push_mode: 'gabc',
      line_breaks: false,
      pop_mode: true,
    });

    const lexer = new Lexer({
      modes: {
        general: [WHITESPACE_DELIMETER, L_PAREN_PUSHING_GABC].concat(tokenCollections.general),
        gabc: [NABC_BEGIN_PIPE].concat(tokenCollections.gabc),
        nabc_gall: [GABC_BEGIN_PIPE].concat(tokenCollections.nabc_gall),
        nabc_laon: [GABC_BEGIN_PIPE].concat(tokenCollections.nabc_laon),
      },
      defaultMode: 'general',
    });

    return { lexer: lexer, pipes: { GABC_BEGIN_PIPE, NABC_BEGIN_PIPE } };
  }
}

export {
  LexerFactory,
  WHITESPACE_DELIMETER,
  R_PAREN_POPPING,
  L_PAREN_PUSHING_GABC,
  L_PAREN_PUSHING_NABC_GALL,
  L_PAREN_PUSHING_NABC_LAON,
};
